// frontend/src/infrastructure/hid/WebHidPttService.ts
//
// Uniwersalna obsługa fizycznego przycisku PTT podłączanego przez USB (WebHID)
// — "gruszka" USB używana w terenie, niezależna od konkretnego producenta /
// vendorId. WebHID to niskopoziomowe API dostępne wyłącznie w przeglądarkach
// opartych na Chromium (Chrome/Edge) za bezpiecznym kontekstem (HTTPS/localhost)
// — Safari i Firefox go NIE wspierają, więc każda metoda tej klasy musi
// bezpiecznie znosić brak `navigator.hid` oraz brak uprawnień użytkownika.
//
// Warstwa infrastruktury: żaden inny moduł nie woła `navigator.hid` bezpośrednio.
// Kompozycję z pozostałymi kanałami PTT (Media Session, symulacja klawiaturowa)
// robi `usePttManager` (presentation/hooks) — ta klasa zna WYŁĄCZNIE WebHID.

/**
 * Minimalne typy WebHID — szkic W3C (https://wicg.github.io/webhid/) nie jest
 * częścią standardowej biblioteki `lib.dom` TypeScript, więc deklarujemy tu
 * tylko to, czego faktycznie używamy.
 */
export interface HIDDeviceFilter {
  vendorId?: number
  productId?: number
  usagePage?: number
  usage?: number
}

export interface HIDInputReportEvent {
  readonly device: HIDDevice
  readonly reportId: number
  readonly data: DataView
}

export interface HIDDevice {
  readonly opened: boolean
  readonly vendorId: number
  readonly productId: number
  readonly productName: string
  open(): Promise<void>
  close(): Promise<void>
  addEventListener(type: 'inputreport', listener: (event: HIDInputReportEvent) => void): void
  removeEventListener(type: 'inputreport', listener: (event: HIDInputReportEvent) => void): void
}

export interface HIDConnectionEvent {
  readonly device: HIDDevice
}

export interface HID {
  requestDevice(options: { filters: readonly HIDDeviceFilter[] }): Promise<HIDDevice[]>
  getDevices(): Promise<HIDDevice[]>
  addEventListener(
    type: 'connect' | 'disconnect',
    listener: (event: HIDConnectionEvent) => void,
  ): void
  removeEventListener(
    type: 'connect' | 'disconnect',
    listener: (event: HIDConnectionEvent) => void,
  ): void
}

declare global {
  interface Navigator {
    /** Obecne wyłącznie w przeglądarkach Chromium za bezpiecznym kontekstem. */
    readonly hid?: HID
  }
}

/**
 * Domyślna heurystyka „wciśnięcia": prosty przycisk/pedał USB zwykle zgłasza
 * stan jednym bajtem (0x00 = puszczony, cokolwiek innego = wciśnięty), a przy
 * kilkubajtowym raporcie każdy niezerowy bajt też liczymy jako wciśnięcie.
 * Wystarcza dla typowych urządzeń HID „jeden przycisk"; dla nietypowej gruszki
 * konsument może podać własny `isPressedReport`.
 */
function defaultIsPressedReport(data: DataView): boolean {
  for (let i = 0; i < data.byteLength; i++) {
    if (data.getUint8(i) !== 0) return true
  }
  return false
}

export interface WebHidPttServiceOptions {
  /** Wołane, gdy raport wejściowy wskazuje WCIŚNIĘCIE przycisku (idempotentne). */
  onPress: () => void
  /** Wołane, gdy raport wejściowy wskazuje PUSZCZENIE przycisku (idempotentne). */
  onRelease: () => void
  /**
   * Filtr urządzeń pokazywanych w natywnym oknie wyboru `requestDevice()`.
   * Domyślnie puste (`[]`) — pokazuje WSZYSTKIE dostępne urządzenia HID, bo
   * "gruszka" jest uniwersalna: nie znamy z góry vendorId/productId
   * konkretnego egzemplarza używanego w terenie.
   */
  filters?: readonly HIDDeviceFilter[]
  /** Rozstrzyga, czy dany raport oznacza wciśnięcie. Domyślnie {@link defaultIsPressedReport}. */
  isPressedReport?: (data: DataView) => boolean
}

/** Wynik {@link WebHidPttService.requestDevice} — pole `reason` mówi, co poszło nie tak. */
export type WebHidRequestOutcome =
  | { ok: true }
  | { ok: false; reason: 'unsupported' | 'no-device-selected' | 'open-failed' }

/**
 * Nasłuchuje fizycznego przycisku PTT podłączonego jako urządzenie USB HID
 * ("gruszka"). Jedna instancja może obsługiwać kilka jednocześnie podłączonych
 * urządzeń — `onPress`/`onRelease` traktują to jako JEDEN wirtualny przycisk:
 * `onRelease` woła się dopiero, gdy WSZYSTKIE podłączone urządzenia wrócą do
 * stanu spoczynku (spójnie z ideą "kilku wejść -> jeden strumień PTT").
 */
export class WebHidPttService {
  private readonly onPress: () => void
  private readonly onRelease: () => void
  private readonly filters: readonly HIDDeviceFilter[]
  private readonly isPressedReport: (data: DataView) => boolean

  private readonly devices = new Set<HIDDevice>()
  private readonly pressedDevices = new Set<HIDDevice>()
  private disconnectListenerAttached = false

  constructor(options: WebHidPttServiceOptions) {
    this.onPress = options.onPress
    this.onRelease = options.onRelease
    this.filters = options.filters ?? []
    this.isPressedReport = options.isPressedReport ?? defaultIsPressedReport
  }

  /** Czy przeglądarka w ogóle udostępnia `navigator.hid` (Chrome/Edge, kontekst bezpieczny). */
  static isSupported(nav: Navigator | undefined = WebHidPttService.currentNavigator()): boolean {
    return typeof nav?.hid !== 'undefined'
  }

  private static currentNavigator(): Navigator | undefined {
    return typeof navigator === 'undefined' ? undefined : navigator
  }

  /** Czy jakiekolwiek urządzenie jest aktualnie podłączone i nasłuchiwane. */
  get isConnected(): boolean {
    return this.devices.size > 0
  }

  /** Nazwy podłączonych urządzeń (do etykiety w UI, np. „Podłączono: Foot Pedal"). */
  get connectedDeviceNames(): readonly string[] {
    return [...this.devices].map((d) => d.productName || 'Urządzenie USB')
  }

  /**
   * Otwiera natywne okno wyboru urządzenia (`navigator.hid.requestDevice`) —
   * MUSI być wołane z bezpośredniej reakcji na gest użytkownika (kliknięcie),
   * inaczej przeglądarka odrzuci wywołanie. Bezpiecznie znosi: brak wsparcia
   * WebHID, zamknięcie okna wyboru bez wybrania urządzenia oraz błąd otwarcia
   * urządzenia (np. zajęte przez inny proces).
   */
  async requestDevice(): Promise<WebHidRequestOutcome> {
    const hid = WebHidPttService.currentNavigator()?.hid
    if (!hid) return { ok: false, reason: 'unsupported' }

    let picked: HIDDevice[]
    try {
      picked = await hid.requestDevice({ filters: this.filters })
    } catch {
      // Użytkownik zamknął okno wyboru bez wybrania urządzenia (niektóre
      // przeglądarki to odrzucają zamiast zwrócić pustą tablicę) albo odmówił
      // uprawnień — z punktu widzenia UI to ten sam, niekrytyczny przypadek.
      return { ok: false, reason: 'no-device-selected' }
    }
    if (picked.length === 0) return { ok: false, reason: 'no-device-selected' }

    let connectedAny = false
    for (const device of picked) {
      const attached = await this.attachDevice(device)
      connectedAny = connectedAny || attached
    }
    return connectedAny ? { ok: true } : { ok: false, reason: 'open-failed' }
  }

  /**
   * Bez pokazywania pickera: podpina się pod urządzenia, na które przeglądarka
   * już wcześniej zapamiętała zgodę użytkownika (persystuje między sesjami w
   * Chrome). Wołać raz przy starcie ekranu, żeby gruszka działała od razu, bez
   * ponownego parowania po każdym odświeżeniu strony.
   */
  async reconnectGrantedDevices(): Promise<number> {
    const hid = WebHidPttService.currentNavigator()?.hid
    if (!hid) return 0

    let granted: HIDDevice[]
    try {
      granted = await hid.getDevices()
    } catch {
      return 0
    }

    let count = 0
    for (const device of granted) {
      const attached = await this.attachDevice(device)
      if (attached) count++
    }
    return count
  }

  /** Odpina wszystkie urządzenia i zdejmuje nasłuchy. Nie cofa uprawnień przeglądarki. */
  dispose(): void {
    for (const device of [...this.devices]) this.detachDevice(device)
    const hid = WebHidPttService.currentNavigator()?.hid
    if (hid && this.disconnectListenerAttached) {
      try {
        hid.removeEventListener('disconnect', this.handleDisconnect)
      } catch {
        // Przeglądarka już i tak nie ma czego czyścić — cichy no-op.
      }
      this.disconnectListenerAttached = false
    }
  }

  private async attachDevice(device: HIDDevice): Promise<boolean> {
    if (this.devices.has(device)) return true
    try {
      if (!device.opened) await device.open()
    } catch {
      return false
    }
    device.addEventListener('inputreport', this.handleInputReport)
    this.devices.add(device)
    this.ensureDisconnectListener()
    return true
  }

  private detachDevice(device: HIDDevice): void {
    if (!this.devices.has(device)) return
    device.removeEventListener('inputreport', this.handleInputReport)
    this.devices.delete(device)
    const wasPressed = this.pressedDevices.delete(device)
    // Odpięte urządzenie liczyło się do stanu „wciśnięty" i było jedynym
    // takim — bez tego dyktowanie zostałoby zawieszone na zawsze po
    // wyjęciu gruszki z USB w trakcie trzymania przycisku.
    if (wasPressed && this.pressedDevices.size === 0) this.onRelease()
  }

  private ensureDisconnectListener(): void {
    if (this.disconnectListenerAttached) return
    const hid = WebHidPttService.currentNavigator()?.hid
    if (!hid) return
    try {
      hid.addEventListener('disconnect', this.handleDisconnect)
      this.disconnectListenerAttached = true
    } catch {
      // Przeglądarka nie wspiera zdarzenia 'disconnect' — niekrytyczne,
      // urządzenie po prostu przestanie wysyłać raporty.
    }
  }

  private readonly handleDisconnect = (event: HIDConnectionEvent): void => {
    this.detachDevice(event.device)
  }

  private readonly handleInputReport = (event: HIDInputReportEvent): void => {
    const pressed = this.isPressedReport(event.data)
    const wasPressed = this.pressedDevices.has(event.device)
    if (pressed === wasPressed) return

    const wasAnyPressed = this.pressedDevices.size > 0
    if (pressed) this.pressedDevices.add(event.device)
    else this.pressedDevices.delete(event.device)
    const isAnyPressed = this.pressedDevices.size > 0

    if (isAnyPressed && !wasAnyPressed) this.onPress()
    else if (!isAnyPressed && wasAnyPressed) this.onRelease()
  }
}
